
import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface FaqItem {
  question: string;
  answer: string;
}

const FaqItem = ({ question, answer }: FaqItem) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="bg-gray-50 rounded-xl overflow-hidden transition-all duration-300 hover:shadow-md">
      <button
        className="w-full text-left p-6 flex justify-between items-center focus:outline-none"
        onClick={() => setIsOpen(!isOpen)}
      >
        <h3 className="text-xl font-semibold text-gray-900">{question}</h3>
        {isOpen ? (
          <ChevronUp className="h-5 w-5 text-edgefi-purple flex-shrink-0" />
        ) : (
          <ChevronDown className="h-5 w-5 text-edgefi-purple flex-shrink-0" />
        )}
      </button>
      
      <div
        className={`px-6 overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-96 pb-6 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <p className="text-gray-600">{answer}</p>
      </div>
    </div>
  );
};

export const FaqSection = () => {
  const faqs: FaqItem[] = [
    {
      question: "How quickly can you respond to security incidents?",
      answer: "Our standard SLAs include immediate response for critical security incidents, with most alerts triaged within 15 minutes. Our dedicated security operations center provides 24/7/365 coverage with multiple tiers of security analysts to ensure rapid response."
    },
    {
      question: "Can we keep our existing security tools and technologies?",
      answer: "Absolutely. We take a vendor-agnostic approach and can work with your existing security investments. We'll evaluate your current tools, identify any gaps, and recommend additions or changes only where they provide clear security or operational benefits."
    },
    {
      question: "How do you handle compliance requirements?",
      answer: "Our managed security services are designed to support a wide range of compliance frameworks including PCI DSS, HIPAA, GDPR, SOC 2, CMMC, and others. We incorporate compliance requirements into our security monitoring, reporting, and control implementation to ensure your regulatory obligations are met."
    },
    {
      question: "What level of visibility do we have into security operations?",
      answer: "We provide comprehensive visibility through a client portal that includes real-time dashboards, security metrics, incident details, and compliance reporting. You'll have access to the same information our analysts use, with the ability to customize reports for different stakeholders in your organization."
    },
    {
      question: "How do you stay ahead of evolving threats?",
      answer: "We maintain a dedicated threat intelligence team that constantly monitors emerging threats, vulnerabilities, and attack techniques. This intelligence is integrated into our detection rules, scanning processes, and analyst training to ensure we can identify and respond to new threats as they emerge. We also participate in industry sharing groups to gain early awareness of threats targeting specific sectors."
    }
  ];

  return (
    <section className="py-16 bg-white" id="faq">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 bg-edgefi-purple/10 text-edgefi-purple rounded-full text-sm font-medium mb-4">FAQ</span>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Common questions about our managed cybersecurity services.
            </p>
          </div>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <FaqItem key={index} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
